import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LockResetIcon from '@mui/icons-material/LockReset';
import { styled, alpha } from '@mui/material/styles';
import { parseError } from '@lov/shared';
import { Button, Input, Alert } from '../ui';
import { useResetPassword } from '../../hooks/auth';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordFormProps {
  token: string;
}

const SuccessCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const SuccessIcon = styled(LockResetIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
  filter: `drop-shadow(0 0 20px ${alpha(theme.palette.success.main, 0.4)})`,
}));

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const navigate = useNavigate();
  const { mutate: resetPassword, isPending, isError, error, isSuccess } = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    resetPassword(
      { token, newPassword: data.password },
      {
        onSuccess: () => {
          setTimeout(() => navigate('/login'), 2000);
        },
      }
    );
  };

  if (isSuccess) {
    return (
      <SuccessCard>
        <SuccessIcon />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Password Reset Successful!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to login page...
        </Typography>
      </SuccessCard>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {isError && (
        <Alert variant="error">
          {parseError(error).message}
        </Alert>
      )}

      <Input
        label="New Password"
        type="password"
        {...register('password')}
        error={errors.password?.message}
        disabled={isPending}
        autoComplete="new-password"
        helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number"
        fullWidth
      />

      <Input
        label="Confirm Password"
        type="password"
        {...register('confirmPassword')}
        error={errors.confirmPassword?.message}
        disabled={isPending}
        autoComplete="new-password"
        fullWidth
      />

      <Button type="submit" fullWidth isLoading={isPending} glow>
        Reset Password
      </Button>
    </Box>
  );
}
